<template>
  <div class="service-settings-container">
    <div class="header">
      <h2>服务管理</h2>
      <el-button type="primary" @click="addCategory" size="small">
        <i class="el-icon-plus"></i> 添加分类
      </el-button>
    </div>

    <div class="service-management">
      <!-- 左侧分类列表 -->
      <div class="category-list">
        <el-menu
            :default-active="activeCategoryId"
            class="category-menu"
            @select="handleCategorySelect"
        >
          <el-menu-item
              v-for="category in categories"
              :key="category.id"
              :index="category.id"
          >
            <i :class="category.icon" style="margin-right: 8px;"></i>
            <span slot="title">{{ category.name }}</span>
            <div class="category-actions">
              <el-button
                  type="text"
                  icon="el-icon-edit"
                  @click.stop="editCategory(category.id)"
              ></el-button>
              <el-button
                  type="text"
                  icon="el-icon-delete"
                  @click.stop="removeCategory(category.id)"
              ></el-button>
            </div>
          </el-menu-item>
        </el-menu>
      </div>

      <!-- 右侧服务列表 -->
      <div class="service-list">
        <div v-if="currentCategory" class="service-list-content">
          <div class="service-list-header">
            <h3>
              <i :class="currentCategory.icon" style="margin-right: 8px;"></i>
              {{ currentCategory.name }} 服务项目
            </h3>
            <el-button type="primary" @click="addService" size="small">
              <i class="el-icon-plus"></i> 添加服务
            </el-button>
          </div>

          <el-table
              :data="currentCategory.services"
              border
              style="width: 100%"
              height="calc(100vh - 250px)"
          >
            <el-table-column prop="name" label="服务名称" width="180"></el-table-column>
            <el-table-column label="基础价格" width="120">
              <template slot-scope="scope">
                ¥{{ scope.row.basePrice }}
              </template>
            </el-table-column>
            <el-table-column label="适用宠物" width="150">
              <template slot-scope="scope">
                <el-tag
                    v-for="pet in scope.row.petTypes"
                    :key="pet"
                    size="small"
                    style="margin-right: 5px;"
                    :type="pet === 'dog' ? 'primary' : 'success'"
                >
                  {{ pet === 'dog' ? '狗狗' : '猫咪' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="服务规格" min-width="300">
              <template slot-scope="scope">
                <div v-for="param in scope.row.params" :key="param.range" class="param-item">
                  <el-tag size="small">{{ param.range }}</el-tag>
                  <span class="param-duration">{{ param.duration }}分钟</span>
                  <span class="param-price">¥{{ param.price }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="150" fixed="right">
              <template slot-scope="scope">
                <el-button
                    size="mini"
                    @click="editService(scope.$index)"
                >编辑</el-button>
                <el-button
                    size="mini"
                    type="danger"
                    @click="removeService(scope.$index)"
                >删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div v-else class="empty-tip">
          <el-empty description="请从左侧选择一个服务分类或添加新分类"></el-empty>
        </div>
      </div>
    </div>

    <!-- 分类编辑对话框 -->
    <el-dialog
        :title="categoryDialogTitle"
        :visible.sync="categoryDialogVisible"
        width="500px"
        :append-to-body="true"
    >
      <el-form
          :model="currentCategoryForm"
          :rules="categoryRules"
          ref="categoryForm"
          label-width="100px"
      >
        <el-form-item label="分类名称" prop="name">
          <el-input v-model="currentCategoryForm.name"></el-input>
        </el-form-item>
        <el-form-item label="分类图标" prop="icon">
          <el-select
              v-model="currentCategoryForm.icon"
              placeholder="请选择图标"
              style="width: 100%"
          >
            <el-option
                v-for="icon in categoryIcons"
                :key="icon.value"
                :label="icon.label"
                :value="icon.value"
            >
              <i :class="icon.value" style="margin-right: 10px;"></i>
              {{ icon.label }}
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="适用宠物" prop="petTypes">
          <el-checkbox-group v-model="currentCategoryForm.petTypes">
            <el-checkbox label="dog">狗狗</el-checkbox>
            <el-checkbox label="cat">猫咪</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="categoryDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="saveCategory">确 定</el-button>
      </span>
    </el-dialog>

    <!-- 服务编辑对话框 -->
    <el-dialog
        :title="serviceDialogTitle"
        :visible.sync="serviceDialogVisible"
        width="700px"
    >
      <el-form
          :model="currentService"
          :rules="serviceRules"
          ref="serviceForm"
          label-width="100px"
      >
        <el-form-item label="服务名称" prop="name">
          <el-input v-model="currentService.name"></el-input>
        </el-form-item>
        <el-form-item label="基础价格" prop="basePrice">
          <el-input-number
              v-model="currentService.basePrice"
              :min="10"
              :max="10000"
              :step="10"
              controls-position="right"
              style="width: 200px"
          ></el-input-number>
        </el-form-item>
        <el-form-item label="适用宠物" prop="petTypes">
          <el-checkbox-group v-model="currentService.petTypes">
            <el-checkbox label="dog">狗狗</el-checkbox>
            <el-checkbox label="cat">猫咪</el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="服务规格">
          <div class="param-form-container">
            <div
                v-for="(param, index) in currentService.params"
                :key="index"
                class="param-form-item"
            >
              <el-input
                  v-model="param.range"
                  placeholder="规格范围(如:10-30斤)"
                  style="width: 200px; margin-right: 10px;"
              ></el-input>
              <el-input-number
                  v-model="param.duration"
                  :min="10"
                  :max="300"
                  :step="10"
                  controls-position="right"
                  placeholder="时长"
                  style="width: 150px; margin-right: 10px;"
              ></el-input-number>
              <el-input-number
                  v-model="param.price"
                  :min="10"
                  :max="10000"
                  :step="10"
                  controls-position="right"
                  placeholder="价格"
                  style="width: 150px; margin-right: 10px;"
              ></el-input-number>
              <span>元</span>
              <el-button
                  type="danger"
                  icon="el-icon-delete"
                  circle
                  size="mini"
                  @click="removeParam(index)"
                  style="margin-left: 10px;"
                  v-if="currentService.params.length > 1"
              ></el-button>
            </div>
            <el-button type="primary" @click="addParam" size="small">
              添加规格
            </el-button>
            <div class="param-tips">
              <p>提示：</p>
              <p>1. 规格范围示例：小型犬(5kg以下)、中型犬(5-15kg)、大型犬(15kg以上)</p>
              <p>2. 价格会根据不同规格单独设置</p>
            </div>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="serviceDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="saveService">确 定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'ServiceSettings',
  props: {
    shop: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      categories: JSON.parse(JSON.stringify(this.shop.services?.categories || [])),
      activeCategoryId: '',
      currentCategory: null,
      categoryDialogVisible: false,
      serviceDialogVisible: false,
      isEditingCategory: false,
      isEditingService: false,
      editingCategoryId: '',
      editingServiceIndex: -1,
      currentCategoryForm: {
        id: '',
        name: '',
        icon: '',
        petTypes: [],
        services: []
      },
      currentService: {
        name: '',
        basePrice: 100,
        petTypes: [],
        params: [
          { range: '10-30斤', duration: 30, price: 100 }
        ]
      },
      categoryIcons: [
        { value: 'el-icon-scissors', label: '剪毛' },
        { value: 'el-icon-water-cup', label: '洗护' },
        { value: 'el-icon-medal', label: '美容' },
        { value: 'el-icon-first-aid-kit', label: '医疗' },
        { value: 'el-icon-place', label: '寄养' },
        { value: 'el-icon-ice-cream-round', label: '零食' },
        { value: 'el-icon-present', label: '礼包' }
      ],
      categoryRules: {
        name: [
          { required: true, message: '请输入分类名称', trigger: 'blur' },
          { min: 2, max: 10, message: '长度在 2 到 10 个字符', trigger: 'blur' }
        ],
        icon: [
          { required: true, message: '请选择分类图标', trigger: 'change' }
        ],
        petTypes: [
          { required: true, message: '请至少选择一种适用宠物', trigger: 'change' }
        ]
      },
      serviceRules: {
        name: [
          { required: true, message: '请输入服务名称', trigger: 'blur' },
          { min: 2, max: 20, message: '长度在 2 到 20 个字符', trigger: 'blur' }
        ],
        basePrice: [
          { required: true, message: '请输入服务基础价格', trigger: 'blur' }
        ],
        petTypes: [
          { required: true, message: '请至少选择一种适用宠物', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    categoryDialogTitle() {
      return this.isEditingCategory ? '编辑服务分类' : '添加服务分类'
    },
    serviceDialogTitle() {
      return this.isEditingService ? '编辑服务项目' : '添加服务项目'
    }
  },
  methods: {
    // 生成唯一ID
    generateId() {
      return Date.now().toString(36) + Math.random().toString(36).substr(2)
    },

    // 分类管理方法
    handleCategorySelect(categoryId) {
      this.activeCategoryId = categoryId
      this.currentCategory = this.categories.find(c => c.id === categoryId)
    },

    addCategory() {
      this.isEditingCategory = false
      this.currentCategoryForm = {
        id: this.generateId(),
        name: '',
        icon: '',
        petTypes: ['dog', 'cat'],
        services: []
      }
      this.categoryDialogVisible = true
      this.$nextTick(() => {
        this.$refs.categoryForm?.clearValidate()
      })
    },

    editCategory(categoryId) {
      const category = this.categories.find(c => c.id === categoryId)
      if (category) {
        this.isEditingCategory = true
        this.editingCategoryId = categoryId
        this.currentCategoryForm = JSON.parse(JSON.stringify(category))
        this.categoryDialogVisible = true
      }
    },

    removeCategory(categoryId) {
      this.$confirm('确定要删除此服务分类吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const index = this.categories.findIndex(c => c.id === categoryId)
        if (index !== -1) {
          this.categories.splice(index, 1)
          if (this.currentCategory?.id === categoryId) {
            this.currentCategory = null
            this.activeCategoryId = ''
          }
          this.$message.success('删除成功')
        }
      })
    },

    saveCategory() {
      this.$refs.categoryForm.validate(valid => {
        if (valid) {
          if (this.isEditingCategory) {
            const index = this.categories.findIndex(c => c.id === this.editingCategoryId)
            if (index !== -1) {
              this.categories.splice(index, 1, this.currentCategoryForm)
            }
          } else {
            this.categories.push(this.currentCategoryForm)
            // 新增分类后自动选中
            this.handleCategorySelect(this.currentCategoryForm.id)
          }
          this.categoryDialogVisible = false
          this.$emit('save', { categories: this.categories })
          this.$message.success('保存成功')
        }
      })
    },

    // 服务管理方法
    addService() {
      if (!this.currentCategory) {
        this.$message.warning('请先选择或创建一个分类')
        return
      }

      this.isEditingService = false
      this.currentService = {
        name: '',
        basePrice: 100,
        petTypes: [...this.currentCategory.petTypes],
        params: [
          { range: '10-30斤', duration: 30, price: 100 }
        ]
      }
      this.serviceDialogVisible = true
      this.$nextTick(() => {
        this.$refs.serviceForm?.clearValidate()
      })
    },

    editService(index) {
      if (!this.currentCategory) return

      this.isEditingService = true
      this.editingServiceIndex = index
      this.currentService = JSON.parse(JSON.stringify(this.currentCategory.services[index]))
      this.serviceDialogVisible = true
    },

    removeService(index) {
      this.$confirm('确定要删除此服务项目吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.currentCategory.services.splice(index, 1)
        this.$emit('save', { categories: this.categories })
        this.$message.success('删除成功')
      })
    },

    saveService() {
      this.$refs.serviceForm.validate(valid => {
        if (valid) {
          // 验证参数范围是否填写完整
          const hasEmptyParam = this.currentService.params.some(
              param => !param.range || !param.duration || !param.price
          )
          if (hasEmptyParam) {
            this.$message.error('请填写完整的规格信息')
            return
          }

          if (this.isEditingService) {
            this.currentCategory.services.splice(
                this.editingServiceIndex,
                1,
                this.currentService
            )
          } else {
            this.currentCategory.services.push(this.currentService)
          }

          this.serviceDialogVisible = false
          this.$emit('save', { categories: this.categories })
          this.$message.success('保存成功')
        }
      })
    },

    // 参数管理方法
    addParam() {
      this.currentService.params.push({
        range: '',
        duration: 30,
        price: this.currentService.basePrice
      })
    },

    removeParam(index) {
      if (this.currentService.params.length > 1) {
        this.currentService.params.splice(index, 1)
      }
    }
  },
  mounted() {
    // 默认选中第一个分类
    if (this.categories.length > 0) {
      this.handleCategorySelect(this.categories[0].id)
    }
  }
}
</script>

<style scoped>
.service-settings-container {
  padding: 20px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.service-management {
  display: flex;
  flex: 1;
  min-height: 0;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
}

.category-list {
  width: 250px;
  border-right: 1px solid #ebeef5;
  background-color: #f5f7fa;
  overflow-y: auto;
}

.category-menu {
  border-right: none;
}

.category-menu .el-menu-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 56px;
  line-height: 56px;
}

.category-actions {
  display: flex;
}

.category-actions .el-button {
  padding: 0;
  margin-left: 5px;
}

.service-list {
  flex: 1;
  padding: 20px;
  background-color: #fff;
  overflow: auto;
}

.service-list-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.service-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.empty-tip {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.param-item {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.param-item .param-duration {
  margin-left: 10px;
  color: #606266;
}

.param-item .param-price {
  margin-left: 10px;
  color: #f56c6c;
  font-weight: bold;
}

.param-form-container {
  width: 100%;
}

.param-form-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.param-tips {
  margin-top: 10px;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
  color: #909399;
  font-size: 12px;
}

.param-tips p {
  margin: 5px 0;
}

.el-tag + .el-tag {
  margin-left: 10px;
}
</style>